import type { StorefrontBuyerCurrency } from '@stokmerter/contracts';
import { useMemo, useState } from 'react';

import {
  colorsImage,
  flatImage,
  formatBuyerPrice,
  lifestyleImages,
  priceMinor,
  primaryImage,
  type CatalogProduct,
  type CatalogViewType,
} from './catalog-types';

export function CatalogProductDetail(props: {
  currency: StorefrontBuyerCurrency;
  initialQuantities: Readonly<Record<string, number>>;
  onApply: (quantities: Readonly<Record<string, number>>) => void;
  onBack: () => void;
  product: CatalogProduct;
  viewType: CatalogViewType;
}) {
  const { currency, initialQuantities, onApply, onBack, product, viewType } = props;
  const [tab, setTab] = useState<'detail' | 'colors'>('detail');
  const [activeImage, setActiveImage] = useState(0);
  const [quantities, setQuantities] = useState<Record<string, number>>({ ...initialQuantities });
  const lifestyle = lifestyleImages(product);
  const gallery = viewType === 'flat' && flatImage(product)
    ? [flatImage(product)!]
    : lifestyle.length > 0 ? lifestyle : [primaryImage(product, viewType)].filter((url): url is string => Boolean(url));
  const colorVisual = colorsImage(product);
  const shownImage = tab === 'colors' && colorVisual ? colorVisual : gallery[activeImage] ?? null;
  const totalSeries = useMemo(() => Object.values(quantities).reduce((sum, value) => sum + value, 0), [quantities]);
  const unitPrice = priceMinor(product, currency);
  const totalPrice = unitPrice === null ? null : unitPrice * totalSeries * product.payload.seriesCount;

  function change(variantId: string, delta: number) {
    setQuantities((current) => ({
      ...current,
      [variantId]: Math.max(0, Math.min(1_000, (current[variantId] ?? 0) + delta)),
    }));
  }

  return <section className="detail-page">
    <div className="detail-heading">
      <button className="detail-back" onClick={onBack} type="button">‹ Ürünlere dön</button>
      <p>Mağaza <span>/</span> {product.payload.category ?? 'Koleksiyon'} <span>/</span> {product.payload.productCode}</p>
      <h1>{product.payload.productName}</h1>
    </div>
    <div className="detail-layout">
      <div className="detail-gallery">
        <div className={`detail-main-image ${tab === 'colors' || viewType === 'flat' ? 'contain' : ''}`}>
          {shownImage ? <img alt={product.payload.productName} src={shownImage} />
            : <span className="image-placeholder">Görsel yok</span>}
          {tab === 'detail' && gallery.length > 1 ? <>
            <button aria-label="Önceki görsel" className="detail-arrow previous" onClick={() =>
              setActiveImage((current) => (current - 1 + gallery.length) % gallery.length)} type="button">‹</button>
            <button aria-label="Sonraki görsel" className="detail-arrow next" onClick={() =>
              setActiveImage((current) => (current + 1) % gallery.length)} type="button">›</button>
          </> : null}
        </div>
        {tab === 'detail' && gallery.length > 1 ? <div className="detail-thumbnails">{gallery.map((url, index) =>
          <button className={activeImage === index ? 'active' : ''} key={url}
            onClick={() => setActiveImage(index)} type="button"><img alt="" src={url} /></button>)}</div> : null}
      </div>
      <div className="detail-purchase">
        <div className="detail-tabs" role="tablist">
          <button aria-selected={tab === 'detail'} className={tab === 'detail' ? 'active' : ''}
            onClick={() => setTab('detail')} role="tab" type="button">Ürün Detayı</button>
          <button aria-selected={tab === 'colors'} className={tab === 'colors' ? 'active' : ''}
            onClick={() => setTab('colors')} role="tab" type="button">Ürün Renkleri</button>
        </div>
        <div className="detail-summary">
          <div><small>Ürün Kodu</small><b>{product.payload.productCode}</b></div>
          <div><small>Seri</small><b>1 seri</b></div>
          <div><small>Adet</small><b>{product.payload.seriesCount} adet</b></div>
          <strong>{formatBuyerPrice(unitPrice, currency)}</strong>
        </div>
        <div className="series-panel">
          <h2>Seri Adedi</h2>
          <div className="series-list">{product.payload.colors.map((color) => {
            const available = color.availability === 'in_stock';
            const quantity = quantities[color.variantId] ?? 0;
            return <div className={`series-row ${available ? '' : 'out'}`} key={color.variantId}>
              <span className="series-color" style={{ backgroundColor: color.hex ?? '#d5d9e2' }} />
              <b>{color.name}</b>
              {!available ? <small>Stok yok</small> : null}
              <div className="quantity-stepper">
                <button aria-label={`${color.name} seri azalt`} disabled={!available || quantity === 0}
                  onClick={() => change(color.variantId, -1)} type="button">−</button>
                <output>{quantity}</output>
                <button aria-label={`${color.name} seri artır`} disabled={!available}
                  onClick={() => change(color.variantId, 1)} type="button">+</button>
              </div>
            </div>;
          })}</div>
          <div className="detail-total">
            <div><span>Toplam</span><b>{totalSeries} seri · {totalSeries * product.payload.seriesCount} adet</b></div>
            {totalPrice !== null ? <strong>{formatBuyerPrice(totalPrice, currency)}</strong> : null}
          </div>
          <button className="add-order-button" disabled={totalSeries === 0}
            onClick={() => onApply(quantities)} type="button">Siparişe ekle</button>
        </div>
      </div>
    </div>
  </section>;
}
